import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { API } from "../App";

/**
 * Handles the Emergent Google Auth redirect.
 * Rendered when the URL contains #session_id=... — exchanges the session for our JWT.
 */
export default function GoogleAuthCallback() {
  const processed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash;
    const params = new URLSearchParams(hash.substring(1));
    const sessionId = params.get("session_id");

    if (!sessionId) {
      window.location.replace("/login");
      return;
    }

    (async () => {
      try {
        const res = await axios.post(`${API}/auth/google/session`, { session_id: sessionId });
        const { token, user, is_first_login } = res.data;
        localStorage.setItem("savi_token", token);
        if (is_first_login) {
          localStorage.setItem("savi_first_login", "true");
        }
        // Full reload so AuthProvider re-initialises with the new token.
        // Route by role: admins go to the admin panel, guests to dashboard/onboarding.
        if (user?.role === "admin") {
          window.location.replace("/admin");
        } else if (user?.onboarding?.final_confirmed) {
          window.location.replace("/dashboard");
        } else {
          window.location.replace(user?.guest_type === "retreat" ? "/retreat-welcome" : "/onboarding");
        }
      } catch (err) {
        const detail = err.response?.data?.detail || "Google sign-in failed. Please try again.";
        setError(detail);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#FBF7F1] flex items-center justify-center px-4">
      {error ? (
        <div className="text-center max-w-sm" data-testid="google-auth-error">
          <p className="font-serif text-2xl text-[#2E2A26] mb-3">Hmm, that didn't work.</p>
          <p className="text-[#2E2A26]/60 text-sm mb-6">{error}</p>
          <a
            href="/login"
            className="inline-block bg-[#C85A38] text-white rounded-full px-8 py-3 text-sm font-medium"
          >
            Back to login
          </a>
        </div>
      ) : (
        <div className="text-center" data-testid="google-auth-loading">
          <Loader2 className="w-8 h-8 animate-spin text-[#C85A38] mx-auto mb-4" />
          <p className="text-[#2E2A26]/60 text-sm">Signing you in with Google...</p>
        </div>
      )}
    </div>
  );
}
